import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Tag, ChevronRight } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import { useT } from '../i18n/LanguageContext';
import { useLocalizedBlogPost, useLocalizedBlogPosts } from '../i18n/contentLocale';
import { useBlogPost, useBlogPosts } from '../hooks/use-blog';
import Seo from '../components/Seo';
import JsonLd from '../components/JsonLd';
import { useLanguage } from '../i18n/LanguageContext';
import { buildLangAlternates, canonicalForLang, blogPostingJsonLd, breadcrumbJsonLd } from '../lib/seo';

export default function BlogDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { formatText } = useCurrency();
  const t = useT();
  const { lang, currentLanguage } = useLanguage();
  const { post: rawPost, isStatic, isLoading } = useBlogPost(id);
  const { posts: allPosts } = useBlogPosts();
  // A renamed post: the API resolved the old slug to its new canonical one.
  const redirectTo = rawPost?.redirect_to;
  useEffect(() => {
    if (redirectTo) navigate(`/blog/${redirectTo}`, { replace: true });
  }, [redirectTo, navigate]);
  // The API localises server-side; the overlay only applies to static content.
  const localizedPost = useLocalizedBlogPost(redirectTo ? null : rawPost);
  const post = isStatic ? localizedPost : (redirectTo ? null : rawPost);

  const relatedRaw = React.useMemo(
    () => (allPosts || []).filter((p) => (p.id ?? p.slug) !== id).slice(0, 3),
    [allPosts, id]
  );
  const localizedRelated = useLocalizedBlogPosts(relatedRaw);
  const related = isStatic ? localizedRelated : relatedRaw;

  // Avoid flashing "not found" while the post is still being fetched.
  if (!post && isLoading) {
    return (
      <div className="pt-24 pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 text-center">
          <div className="mx-auto h-8 w-56 animate-pulse rounded-full bg-slate-100" />
          <div className="mx-auto mt-4 h-4 w-80 animate-pulse rounded-full bg-slate-100" />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="pt-24 pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 text-center">
          <h1 className="text-3xl font-bold text-[#14213d]">
            {t('blogDetail.notFound')}
          </h1>
          <p className="text-gray-500 mt-4">{t('blogDetail.notFoundText')}</p>
          <Link to="/blog" className="inline-flex items-center gap-2 mt-6 text-[#c8a25a] font-semibold hover:underline">
            <ArrowLeft className="w-4 h-4" /> {t('blogDetail.back')}
          </Link>
        </div>
      </div>
    );
  }

  const author = post.author || t('blogDetail.author');
  const readTime = post.readTime || `${Math.max(2, Math.round((post.content || post.excerpt || '').split(/\s+/).length / 200))} ${t('blog.minRead')}`;

  const renderContent = (content) => {
    if (!content) return null;

    const lines = formatText(content).split('\n');
    const elements = [];
    let inList = false;
    let listItems = [];
    let inTable = false;
    let tableRows = [];

    lines.forEach((line, index) => {
      const trimmed = line.trim();

      // Inline markdown: **bold** and [text](url) links.
      const parseInline = (text) => {
        const parts = text.split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*)/g);
        return parts.map((part, i) => {
          const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
          if (link) {
            return link[2].startsWith('/')
              ? <Link key={i} to={link[2]} className="font-semibold text-[#1fa5a3] underline decoration-[#1fa5a3]/40 hover:text-[#c8a25a]">{link[1]}</Link>
              : <a key={i} href={link[2]} target="_blank" rel="noreferrer" className="font-semibold text-[#1fa5a3] underline hover:text-[#c8a25a]">{link[1]}</a>;
          }
          if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
          return <span key={i}>{part}</span>;
        });
      };

      // Horizontal rule
      if (trimmed === '---') {
        elements.push(<hr key={index} className="my-8 border-gray-200" />);
        return;
      }

      // Image ![alt](url)
      const imgMatch = trimmed.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
      if (imgMatch) {
        if (inList) { elements.push(<ul key={`list-${index}`} className="list-disc list-inside text-gray-700 mb-6 space-y-2">{listItems}</ul>); listItems = []; inList = false; }
        elements.push(
          <figure key={index} className="my-8">
            <img src={imgMatch[2]} alt={imgMatch[1]} loading="lazy" className="w-full aspect-[16/9] object-cover rounded-2xl shadow-[0_18px_40px_-24px_rgba(11,33,61,0.3)]" />
            {imgMatch[1] ? <figcaption className="mt-2 text-center text-sm text-gray-400">{imgMatch[1]}</figcaption> : null}
          </figure>
        );
        return;
      }

      // Headers
      if (trimmed.startsWith('## ')) {
        if (inList) { elements.push(<ul key={`list-${index}`} className="list-disc list-inside text-gray-700 mb-6 space-y-2">{listItems}</ul>); listItems = []; inList = false; }
        elements.push(<h2 key={index} className="text-2xl md:text-3xl font-bold text-[#14213d] mt-10 mb-4">{trimmed.replace('## ', '')}</h2>);
        return;
      }
      if (trimmed.startsWith('### ')) {
        if (inList) { elements.push(<ul key={`list-${index}`} className="list-disc list-inside text-gray-700 mb-6 space-y-2">{listItems}</ul>); listItems = []; inList = false; }
        elements.push(<h3 key={index} className="text-xl font-semibold text-[#14213d] mt-8 mb-3">{trimmed.replace('### ', '')}</h3>);
        return;
      }

      // Blockquote
      if (trimmed.startsWith('> ')) {
        if (inList) { elements.push(<ul key={`list-${index}`} className="list-disc list-inside text-gray-700 mb-6 space-y-2">{listItems}</ul>); listItems = []; inList = false; }
        const quoteText = trimmed.replace('> ', '');
        const isHighlight = quoteText.startsWith('**') || quoteText.includes('**¿Listo') || quoteText.includes('**Dato');
        elements.push(
          <blockquote key={index} className={`border-l-4 border-[#c8a25a] pl-6 italic text-gray-600 my-6 ${isHighlight ? 'bg-[#fff5f2] rounded-r-lg p-4' : ''}`}>
            <p className="not-italic">{parseInline(quoteText)}</p>
          </blockquote>
        );
        return;
      }

      // List items
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        if (!inList) inList = true;
        const itemText = trimmed.replace(/^[-*]\s*/, '');
        listItems.push(<li key={index} className="leading-relaxed">{parseInline(itemText)}</li>);
        return;
      } else if (inList) {
        elements.push(<ul key={`list-${index}`} className="list-disc list-inside text-gray-700 mb-6 space-y-2">{listItems}</ul>);
        listItems = [];
        inList = false;
      }

      // Table
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        if (!inTable) { inTable = true; tableRows = []; }
        const cells = trimmed.split('|').slice(1, -1).map(c => c.trim());
        tableRows.push(cells);
        return;
      } else if (inTable) {
        if (tableRows.length > 0) {
          const headers = tableRows[0];
          const rows = tableRows.slice(1);
          elements.push(
            <div key={index} className="overflow-x-auto my-8">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-[#1fa5a3] text-white">
                    {headers.map((h, i) => <th key={i} className="border border-gray-300 px-4 py-3 text-left font-semibold">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, r) => (
                    <tr key={r} className={r % 2 === 0 ? 'bg-gray-50' : ''}>
                      {row.map((cell, c) => <td key={c} className="border border-gray-300 px-4 py-3">{parseInline(cell)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        inTable = false;
        tableRows = [];
      }

      // Regular paragraph
      if (trimmed && !trimmed.startsWith('|')) {
        elements.push(<p key={index} className="text-gray-700 leading-relaxed mb-4">{parseInline(trimmed)}</p>);
      }
    });

    // Flush remaining list/table
    if (inList && listItems.length > 0) {
      elements.push(<ul key="list-end" className="list-disc list-inside text-gray-700 mb-6 space-y-2">{listItems}</ul>);
    }
    if (inTable && tableRows.length > 0) {
      const headers = tableRows[0];
      const rows = tableRows.slice(1);
      elements.push(
        <div key="table-end" className="overflow-x-auto my-8">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-[#1fa5a3] text-white">
                {headers.map((h, i) => <th key={i} className="border border-gray-300 px-4 py-3 text-left font-semibold">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={r} className={r % 2 === 0 ? 'bg-gray-50' : ''}>
                  {row.map((cell, c) => <td key={c} className="border border-gray-300 px-4 py-3">{parseInline(cell)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return <div className="prose prose-gray max-w-none">{elements}</div>;
  };

  return (
    <article className="pt-24 pb-20 bg-white">
      <Seo
        title={post.seoTitle || `${post.title} | OWA Wild Adventure`}
        description={post.seoDescription || post.excerpt}
        image={post.image}
        canonical={canonicalForLang(`/blog/${post.id}`, lang)}
        alternates={buildLangAlternates(`/blog/${post.id}`)}
        ogLocale={currentLanguage.locale.replace('-', '_')}
        type="article"
      />
      <JsonLd data={blogPostingJsonLd(post, `/blog/${post.id}`)} />
      <JsonLd data={breadcrumbJsonLd([
        { name: t('nav.home'), path: '/' },
        { name: t('nav.blog'), path: '/blog' },
        { name: post.title, path: `/blog/${post.id}` },
      ])} />
      <div className="max-w-4xl mx-auto px-5 md:px-8">
        {/* Breadcrumb */}
        <nav className="mb-8 flex items-center gap-2 text-sm text-gray-500" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-[#c8a25a] transition-colors">{t('nav.home')}</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/blog" className="hover:text-[#c8a25a] transition-colors">{t('nav.blog')}</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-[#14213d] font-medium truncate max-w-[200px]">{post.title}</span>
        </nav>

        {/* Post Header */}
        <header className="mb-10">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="px-3 py-1 rounded-md bg-[#1fa5a3]/10 text-[11px] font-bold tracking-wider text-[#1fa5a3]">{post.category}</span>
            {post.tags && post.tags.map(tag => (
              <span key={tag} className="px-2 py-1 rounded bg-gray-100 text-[11px] font-medium text-gray-600">#{tag}</span>
            ))}
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-[#14213d] leading-tight mb-6">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-6 text-gray-500 text-sm">
            <div className="flex items-center gap-1.5">
              <User className="w-4 h-4" />
              <span>{author}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              <span>{post.date}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              <span>{readTime}</span>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        <figure className="mb-12">
          <img 
            src={post.image} 
            alt={post.title} 
            className="w-full aspect-[16/9] object-cover rounded-2xl shadow-[0_20px_40px_-20px_rgba(11,33,61,0.2)]"
          />
        </figure>

        {/* Post Content */}
        <div className="text-gray-700 leading-relaxed text-lg">
          {post.content ? renderContent(post.content) : (
            <p className="text-gray-500">{t('blogDetail.noContent')}</p>
          )}
        </div>

        {/* CTA Section */}
        {post.id === 'whale-species-canary-islands' && (
          <div className="mt-16 p-8 rounded-2xl bg-gradient-to-r from-[#14213d] to-[#1fa5a3] text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              {t('blogDetail.ctaTitle')}
            </h2>
            <p className="text-[#1fa5a3]/90 mb-6 max-w-2xl">
              {t('blogDetail.ctaText')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/activity/ocean-giants-cruise"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-[#14213d] font-semibold hover:bg-gray-100 transition-colors"
              >
                {t('blogDetail.ctaBook')}
                <ChevronRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border-2 border-white text-white font-semibold hover:bg-white/10 transition-colors"
              >
                {t('blogDetail.ctaAsk')}
              </Link>
            </div>
          </div>
        )}

        {/* Related posts */}
        {related.length > 0 && (
          <section className="mt-16 pt-10 border-t border-gray-200">
            <h2 className="text-2xl font-bold text-[#14213d] mb-6">{t('blogDetail.keepReading')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.id} to={`/blog/${p.id}`} className="group block overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-[0_14px_36px_-26px_rgba(11,33,61,0.4)] hover:-translate-y-0.5 transition-all">
                  <div className="aspect-[16/10] overflow-hidden bg-gray-100">
                    <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-4">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-[#1fa5a3]">{p.category}</span>
                    <h3 className="mt-1 text-[15px] font-semibold leading-snug text-[#14213d] line-clamp-2 group-hover:text-[#1fa5a3]">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Back to Blog */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[#c8a25a] font-semibold hover:underline transition-colors">
            <ArrowLeft className="w-4 h-4" /> {t('blogDetail.back')}
          </Link>
        </div>
      </div>
    </article>
  );
}